"use client";

import type { CSSProperties } from "react";
import { useT } from "@/lib/i18n";
import { useSystemStore } from "@/lib/store";

export function MobileDetailsPill({
  anchor,
  style,
}: {
  anchor?: { kind: "node" | "cable"; id: string };
  style?: CSSProperties;
}) {
  const tr = useT();
  const setMobileDetailsOpen = useSystemStore((s) => s.setMobileDetailsOpen);
  // Every selected item opens the same sheet, including its bulk actions.
  // Keep one opener on the first node, or first cable for cable-only selections.
  const isAnchor = useSystemStore((s) => {
    if (!anchor) return true;
    if (anchor.kind === "node") return s.selection.nodeIds[0] === anchor.id;
    return s.selection.nodeIds.length === 0 && s.selection.cableIds[0] === anchor.id;
  });
  if (!isAnchor) return null;

  return (
    <button
      type="button"
      aria-label={tr("mobile.details.open")}
      onClick={(event) => {
        event.stopPropagation();
        // Programmatic/browser-assisted clicks do not always move focus first.
        // Give the dialog focus scope a stable element to restore on close.
        event.currentTarget.focus();
        setMobileDetailsOpen(true);
      }}
      onPointerDown={(event) => event.stopPropagation()}
      className="nodrag nopan"
      style={{
        pointerEvents: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 44,
        padding: "0 10px 0 11px",
        borderRadius: 22,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-strong)",
        fontSize: 11,
        lineHeight: 1,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        color: "var(--color-fg)",
        boxShadow: "0 2px 6px hsl(30 8% 10% / 0.10)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {tr("mobile.details.label")}
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
